import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  ArrowPathIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FolderPlusIcon,
  InformationCircleIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { type UploadResult, type UploadSkipReason, sanitizeDestFolder } from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { useI18n, type MessageKey } from '../i18n';
import { interpolate } from '../gallery/format';
import { formatBytes } from '../viewer/format';
import { uploadFiles } from './organize-api';
import { useAllFolders } from './use-all-folders';
import { buildFolderTree, composeNewFolderPath, type FolderNode } from './folder-tree';
import { FolderTreePicker, type FolderTreePickerLabels } from './FolderTreePicker';
import { readLastUploadFolder, storeLastUploadFolder } from './upload-pref';

// Re-export du helper partagé (déplacé dans `folder-tree`) pour préserver les imports
// existants depuis ce module et le test du téléversement.
export { composeNewFolderPath } from './folder-tree';

/**
 * Modale de TÉLÉVERSEMENT de fichiers dans la bibliothèque (XPHOTO-16).
 *
 * - Sélection MULTI-fichiers : `<input type="file" multiple>` + zone de glisser-déposer ;
 *   la liste des fichiers choisis (nom + taille) est éditable (retrait avant envoi).
 * - Destination : UN SEUL arbre de dossiers (la racine, sélectionnable, est proposée en
 *   tête). Un BANDEAU récapitule la destination choisie et propose « Nouveau dossier ici » :
 *   une carte de confirmation inline crée un sous-dossier DANS la destination courante (nom
 *   validé en direct par `sanitizeDestFolder`). Le dossier est créé IMPLICITEMENT côté
 *   serveur au moment du téléversement — aucun appel de création séparé. Par défaut la
 *   destination est le dossier OUVERT dans la galerie (`currentFolder`), sinon le DERNIER
 *   dossier utilisé (persistant), sinon la racine.
 * - Envoi : POST FormData → le serveur écrit les fichiers puis enfile un job `scan` pour
 *   les indexer (suivi dans la cloche). Résultats PAR FICHIER (importés / ignorés + raison).
 *
 * Boîte de dialogue accessible : `role="dialog"` + `aria-modal`, focus initial/restauré,
 * verrou de défilement, Échap ferme. Le caller l'enveloppe dans un `<Portal>`.
 */
interface UploadModalProps {
  libraryId: string;
  /**
   * Dossier ACTUELLEMENT ouvert dans la galerie (préfixe POSIX), ou `undefined` si la
   * vue n'est pas dans un dossier → défaut = dernier dossier utilisé, sinon racine.
   */
  currentFolder?: string | undefined;
  onClose: () => void;
  /** Appelé après un téléversement réussi (au moins un fichier écrit) : rafraîchir. */
  onUploaded?: () => void;
}

/**
 * Clés i18n des raisons d'exclusion d'un fichier (message localisé par raison).
 * `Record` clé par `UploadSkipReason` : le typecheck impose l'exhaustivité à chaque
 * raison ajoutée au contrat partagé.
 */
const SKIP_REASON_KEY: Record<UploadSkipReason, MessageKey> = {
  non_media: 'upload.skipNonMedia',
  bad_name: 'upload.skipBadName',
  empty: 'upload.skipEmpty',
  write_error: 'upload.skipWriteError',
  convert_failed: 'upload.skipConvertFailed',
  too_large: 'upload.skipTooLarge',
};

/**
 * Destination par défaut à l'ouverture : dossier ouvert dans la galerie, sinon dernier
 * dossier utilisé (persistant), sinon racine (`''`). PUR (facilite le test).
 */
export function resolveDefaultUploadFolder(currentFolder: string | undefined): string {
  if (currentFolder !== undefined) return currentFolder;
  const last = readLastUploadFolder();
  return last ?? '';
}

export function UploadModal({
  libraryId,
  currentFolder,
  onClose,
  onUploaded,
}: UploadModalProps): React.JSX.Element {
  const { t, locale } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const newNameInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Destination sélectionnée (`''` = racine). Source de vérité UNIQUE : alimente le submit
  // ET le `selected` du picker. Un dossier « nouveau » (absent de l'arbre) y est aussi posé.
  const [dest, setDest] = useState(() => resolveDefaultUploadFolder(currentFolder));
  // Carte « Nouveau dossier ici » : ouverte ? + nom du sous-dossier saisi.
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newName, setNewName] = useState('');
  // Arbre des dossiers PARTAGÉ (audit A9) : rouvrir la modale ne re-liste plus tout.
  const folders = useAllFolders(libraryId);
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  useModalA11y({ dialogRef, initialFocusRef: fileInputRef, onEscape: !busy ? onClose : undefined });
  const [enlarged, setEnlarged] = useState(false);
  const [error, setError] = useState<'writeDisabled' | 'forbidden' | 'generic' | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);

  // Destination effective (miroir client de la règle serveur) : la racine (`''`) est valide.
  const destNormalized = dest === '' ? '' : sanitizeDestFolder(dest);
  const destValid = destNormalized !== null;
  // Libellé affiché de la destination : le chemin, ou le libellé racine si racine.
  const destDisplay = dest === '' ? t('folders.root') : dest;

  // Carte de création : chemin composé (parent = destination courante) + version normalisée.
  const newRawPath = composeNewFolderPath(dest, newName);
  const newNormalized = newRawPath === '' ? null : sanitizeDestFolder(newRawPath);
  const newNameEmpty = newName.trim() === '';

  // Verrou de défilement + focus initial + restauration.

  // À l'ouverture de la carte, place le focus dans le champ « nom ».
  useEffect(() => {
    if (creatingFolder) newNameInputRef.current?.focus();
  }, [creatingFolder]);

  // Échap ferme (sauf pendant l'appel réseau).

  const tree: FolderNode[] = useMemo(
    () => (folders.status === 'ready' ? buildFolderTree(folders.entries) : []),
    [folders],
  );

  // Libellés du sélecteur de dossier repliable (mémorisés — dépendent de la locale).
  const pickerLabels: FolderTreePickerLabels = useMemo(
    () => ({
      search: t('upload.searchPlaceholder'),
      loading: t('upload.loadingFolders'),
      error: t('upload.foldersError'),
      root: t('folders.root'),
      selectFolder: t('upload.selectFolder'),
      folderCount: t('upload.folderCount'),
      expand: t('upload.expandFolder'),
      collapse: t('upload.collapseFolder'),
      searchEmpty: t('upload.searchEmpty'),
    }),
    [t],
  );

  /** Ajoute des fichiers à la sélection (dédoublonnés par nom+taille). */
  const addFiles = useCallback((incoming: FileList | File[]): void => {
    const list = Array.from(incoming);
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const merged = [...prev];
      for (const f of list) {
        const key = `${f.name}:${f.size}`;
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(f);
        }
      }
      return merged;
    });
  }, []);

  const removeFile = useCallback((index: number): void => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /** Ouvre la carte de création d'un sous-dossier (nom vierge). */
  const openCreateFolder = useCallback((): void => {
    setNewName('');
    setCreatingFolder(true);
  }, []);

  /** Ferme la carte SANS rien changer à la destination. */
  const cancelCreateFolder = useCallback((): void => {
    setCreatingFolder(false);
    setNewName('');
  }, []);

  /** Valide la carte : pose la destination = nouveau chemin normalisé, puis referme. */
  const confirmCreateFolder = useCallback((): void => {
    if (newNormalized === null) return;
    setDest(newNormalized);
    setCreatingFolder(false);
    setNewName('');
  }, [newNormalized]);

  const onSubmit = useCallback(async () => {
    if (!destValid || destNormalized === null || busy || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await uploadFiles(libraryId, destNormalized, files);
      setResult(res);
      // Persiste le dossier réellement utilisé (normalisé par le serveur).
      storeLastUploadFolder(res.destFolder);
      if (res.uploaded > 0) onUploaded?.();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'WRITE_DISABLED') {
        setError('writeDisabled');
      } else if (err instanceof ApiError && err.status === 403) {
        setError('forbidden');
      } else {
        setError('generic');
      }
    } finally {
      setBusy(false);
    }
  }, [destValid, destNormalized, busy, files, libraryId, onUploaded]);

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('upload.title')}
        className={`flex max-h-full w-full flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl transition-[max-width] ${
          enlarged ? 'max-w-3xl' : 'max-w-lg'
        }`}
      >
        {/* En-tête. */}
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <ArrowUpTrayIcon className="h-6 w-6 text-brand" aria-hidden="true" />
            {t('upload.title')}
          </h2>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setEnlarged((v) => !v)}
              title={enlarged ? t('upload.reduce') : t('upload.enlarge')}
              aria-label={enlarged ? t('upload.reduce') : t('upload.enlarge')}
              aria-pressed={enlarged}
              className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {enlarged ? (
                <ArrowsPointingInIcon className="h-6 w-6" aria-hidden="true" />
              ) : (
                <ArrowsPointingOutIcon className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              title={t('upload.close')}
              aria-label={t('upload.close')}
              className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
            >
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Corps. */}
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-4">
          {result !== null ? (
            <UploadResultView result={result} onClose={onClose} />
          ) : (
            <>
              <p className="text-sm text-muted">{t('upload.intro')}</p>
              {/* Note HEIC : la conversion est IRRÉVERSIBLE et l'original n'est pas conservé →
                  visible AVANT le dépôt des fichiers, pas découverte dans le résultat. */}
              <p className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-muted">
                <InformationCircleIcon
                  className="mt-0.5 h-5 w-5 shrink-0 text-brand"
                  aria-hidden="true"
                />
                {t('upload.heicNote')}
              </p>

              {/* Zone de dépôt + input fichiers. */}
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions --
                  Seuls des gestionnaires de GLISSER-DÉPOSER sont posés ici (aucun
                  `onClick`) : le dépôt est un enrichissement souris/tactile, et le
                  choix des fichiers reste entièrement réalisable au clavier via
                  l'`<input type="file">` étiqueté que cette zone contient. */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!busy) setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  if (!busy && e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
                }}
                className={`flex flex-col items-center gap-2 rounded-md border-2 border-dashed p-6 text-center ${
                  dragOver ? 'border-brand bg-brand/10' : 'border-border'
                }`}
              >
                <ArrowUpTrayIcon className="h-8 w-8 text-muted" aria-hidden="true" />
                <p className="text-sm text-muted">{t('upload.dropHint')}</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  // Jokers MIME (jamais la liste d'extensions) : sur mobile, ils font
                  // proposer Appareil photo / Galerie / Fichiers. Sans `accept`, Android
                  // n'offre souvent que les Fichiers + l'appareil photo (pas la galerie).
                  // Pas de `capture` (forcerait l'appareil photo). L'allow-list exacte
                  // reste appliquée côté serveur (`isMediaExtension` → `skipped`).
                  accept="image/*,video/*"
                  disabled={busy}
                  onChange={(e) => {
                    if (e.target.files) addFiles(e.target.files);
                    // Réinitialise pour permettre de re-choisir le même fichier après un retrait.
                    e.target.value = '';
                  }}
                  aria-label={t('upload.chooseFiles')}
                  className="block w-full text-sm text-fg file:mr-3 file:rounded-md file:border-0 file:bg-brand file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand-fg hover:file:bg-brand/90 disabled:opacity-50"
                />
              </div>

              {/* Liste des fichiers choisis (retrait avant envoi). */}
              {files.length > 0 && (
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium text-fg">
                    {interpolate(t('upload.selectedCount'), { count: files.length }, locale)}
                  </p>
                  <ul className="max-h-40 overflow-y-auto rounded-md border border-border">
                    {files.map((file, index) => (
                      <li
                        key={`${file.name}:${file.size}:${index}`}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-fg"
                      >
                        <PhotoIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
                        <span className="min-w-0 flex-1 truncate" title={file.name}>
                          {file.name}
                        </span>
                        <span className="shrink-0 text-xs text-muted">
                          {formatBytes(file.size, locale)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          disabled={busy}
                          aria-label={interpolate(t('upload.removeFile'), { name: file.name })}
                          title={interpolate(t('upload.removeFile'), { name: file.name })}
                          className="rounded-md p-0.5 text-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                        >
                          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Destination : UN SEUL arbre + bandeau récap + carte « Nouveau dossier ici ». */}
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-fg">{t('upload.destLabel')}</p>

                <FolderTreePicker
                  status={folders.status}
                  nodes={tree}
                  selected={destNormalized}
                  onSelect={(path) => setDest(path)}
                  busy={busy}
                  enlarged={enlarged}
                  labels={pickerLabels}
                />

                {/* Bandeau : destination sélectionnée + action « Nouveau dossier ici ». */}
                <div className="flex items-center justify-between gap-3 rounded-md bg-surface-2 p-3">
                  <div className="min-w-0">
                    <p className="text-xs text-muted">{t('upload.destSelected')}</p>
                    <p className="truncate text-sm font-medium text-fg" title={destDisplay}>
                      {destDisplay}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={openCreateFolder}
                    disabled={busy}
                    className="inline-flex shrink-0 items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                  >
                    <FolderPlusIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    {t('upload.newFolderHere')}
                  </button>
                </div>

                {/* Carte de confirmation inline : créer un sous-dossier DANS la destination. */}
                {creatingFolder && (
                  <div className="flex flex-col gap-3 rounded-md border border-brand bg-brand/10 p-3">
                    <p
                      id="upload-newfolder-question"
                      className="inline-flex items-start gap-2 text-sm font-medium text-fg"
                    >
                      <FolderPlusIcon
                        className="mt-0.5 h-5 w-5 shrink-0 text-brand"
                        aria-hidden="true"
                      />
                      {interpolate(t('upload.newFolderConfirm'), { parent: destDisplay })}
                    </p>

                    <div className="flex flex-col gap-1">
                      <label htmlFor="upload-newname-input" className="text-sm font-medium text-fg">
                        {t('upload.newFolderNameLabel')}
                      </label>
                      <input
                        id="upload-newname-input"
                        ref={newNameInputRef}
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        disabled={busy}
                        autoComplete="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        placeholder={t('upload.newFolderNamePlaceholder')}
                        aria-invalid={!newNameEmpty && newNormalized === null}
                        aria-describedby="upload-newfolder-question upload-newname-preview"
                        className="h-11 rounded-md border border-border bg-surface px-3 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                      />
                      {/* Aperçu vivant du chemin résultant OU message d'erreur OU hint. */}
                      {newNameEmpty ? (
                        <span id="upload-newname-preview" className="text-xs text-muted">
                          {t('upload.newFolderNameHint')}
                        </span>
                      ) : newNormalized !== null ? (
                        <span
                          id="upload-newname-preview"
                          className="inline-flex items-center gap-1 text-xs text-muted"
                        >
                          <FolderPlusIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                          {interpolate(t('upload.newFolderPreview'), { path: newNormalized })}
                        </span>
                      ) : (
                        <p
                          id="upload-newname-preview"
                          className="inline-flex items-center gap-1 text-sm text-danger"
                        >
                          <ExclamationTriangleIcon
                            className="h-5 w-5 shrink-0"
                            aria-hidden="true"
                          />
                          {t('upload.destInvalid')}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={cancelCreateFolder}
                        disabled={busy}
                        className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                      >
                        {t('upload.cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={confirmCreateFolder}
                        disabled={busy || newNormalized === null}
                        className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <FolderPlusIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                        {t('upload.newFolderCreate')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {error !== null && (
                <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
                  <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {error === 'writeDisabled'
                    ? t('upload.errorWriteDisabled')
                    : error === 'forbidden'
                      ? t('upload.errorForbidden')
                      : t('upload.error')}
                </p>
              )}
            </>
          )}
        </div>

        {/* Actions. */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
          {result !== null ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {t('upload.close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              >
                {t('upload.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void onSubmit()}
                disabled={!destValid || busy || files.length === 0}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy && (
                  <ArrowPathIcon
                    className="h-5 w-5 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                )}
                {busy ? t('upload.uploading') : t('upload.submit')}
              </button>
            </>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}

/** Vue de résultat : compte des importés + liste des ignorés (raison localisée). */
function UploadResultView(props: { result: UploadResult; onClose: () => void }): React.JSX.Element {
  const { t, locale } = useI18n();
  const { result } = props;
  return (
    <div className="flex flex-col gap-3">
      <p
        role="status"
        className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-fg"
      >
        <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
        <span>
          {interpolate(t('upload.resultUploaded'), { count: result.uploaded }, locale)}
          {result.jobId !== undefined ? ` ${t('upload.resultIndexing')}` : ''}
        </span>
      </p>
      {result.skipped.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="inline-flex items-center gap-1 text-sm font-medium text-fg">
            <ExclamationTriangleIcon
              className="h-5 w-5 shrink-0 text-warning-fg"
              aria-hidden="true"
            />
            {interpolate(t('upload.resultSkipped'), { count: result.skipped.length }, locale)}
          </p>
          <ul className="max-h-40 overflow-y-auto rounded-md border border-border">
            {result.skipped.map((s, i) => (
              <li
                key={`${s.filename}:${i}`}
                className="flex items-center gap-2 px-3 py-2 text-sm text-fg"
              >
                <span className="min-w-0 flex-1 truncate" title={s.filename}>
                  {s.filename}
                </span>
                <span className="shrink-0 text-xs text-muted">{t(SKIP_REASON_KEY[s.reason])}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
